import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row, Container } from 'react-bootstrap';
import { TextInput, RadioInput, CheckboxInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs'
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import BackNext from '../../Common/SubComponents/BackNext';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY } from '../../../../sessionStorage';
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import { InputMaskZip, InputMaskDate, InputMaskPhone } from '../../../Helpers/Formatters';
import { customValidators, object, string, mixed } from '../../../Helpers/Validators/customValidators'
import CalculatePolicyEffectiveDate from '../../../Helpers/Dates/Americo/PolicyDate';
import { Dialog, DialogContent, IconButton, Button, CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Step10Icon from '../icons/step_10.svg';
import IconHolder from '../../../Helpers/IconHolder';

export default function FinancialInformation(props) {

  const [loading, setLoading] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const [routingLoading, setRoutingLoading] = useState('');
  const [hasRoutingNumberError, setHasRoutingNumberError] = useState(false);
  const [financialInstitution, setFinancialInstitution] = useState('');
  const routingErrorMessage = useRef();

  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: FinancialInformation.ValidationSchema(props.ageSpecs, props.fullFormState.steps.basicInfo.birthdate),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    register({ name: 'accountType' });
    setValue('accountType', props.formState.accountType);

    register({ name: 'payor' });
    setValue('payor', props.formState.payor);

    register({ name: 'payorMailingState' });
    setValue('payorMailingState', props.formState.payorMailingState);

    register({ name: 'payorStreetState' });
    setValue('payorStreetState', props.formState.payorStreetState);

    register({ name: 'specificStartDate' });
    setValue('specificStartDate', props.formState.specificStartDate);

    register({ name: 'businessAccount' });
    setValue('businessAccount', props.formState.businessAccount);

    register({ name: 'signingAuthority' });
    setValue('signingAuthority', props.formState.signingAuthority);

    register({ name: 'mailingAdressISPOBox' });
    setValue('mailingAdressISPOBox', props.formState.mailingAdressISPOBox);
  }, [register])

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  const onNext = () => {

    if (hasRoutingNumberError) {
      window.scrollTo(0, routingErrorMessage.current.offsetTop);
      return;
    }

    setLoading(true);

    fetch(`${covrServicesUrl}/api/Americo/CreateInitialCase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
      },
      body: JSON.stringify(props.fullFormState) //test formstate being updated prior with payment info
    })
      .then(res => res.json())
      .then(json => {
        if (json.appIdentifier) {
          let draftDate;
          if (props.formState.draftPreference === 'Specific') {
            draftDate = new Date(props.formState.specificStartDate);
          }
          else {
            draftDate = new Date();
          }

          props.handlePaymentComplete(json.appIdentifier, CalculatePolicyEffectiveDate(draftDate));
        }
        else {
          setLoading(false)
        }
      })
  }

  const routingNumberCheck = () => {
    setFinancialInstitution('');
    setHasRoutingNumberError(false);

    const regex = RegExp(/^[0-9]{9}$/);

    const isValidRouting = regex.test(props.formState.routingNumber);

    if (isValidRouting) {
      setRoutingLoading(<CircularProgress size={25} thickness={4.5} />);

      fetch(`${covrServicesUrl}/api/Americo/GetBankNameFromRoutingNumber?routingNumber=${props.formState.routingNumber}`)
        .then(res => res.json())
        .then(json => {
          if (json.success) {
            setFinancialInstitution(json.bankName)
            setRoutingLoading('');
          }
          else if (!json.success) {
            setHasRoutingNumberError(true);
            setRoutingLoading('');
          }
        })
    }
  }

  // app id must be set before proceeding to esign
  useEffect(() => {
    if (props.fullFormState.appIdentifier && loading) {
      props.onNext();
    }
  }, [props.fullFormState.appIdentifier]);

  useEffect(() => {
    handleValueChange('institutionName', financialInstitution)
  }, [financialInstitution]);

  let debounce = null;
  useEffect(() => {
    let isSubscribed = true;
    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => routingNumberCheck(() => isSubscribed), 1000);
    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    }
  }, [props.formState.routingNumber])

  return (
    <Container>
      <Form className="quote-form" onSubmit={handleSubmit(onNext)}>
      <Row className="formPage justify-content-center">
        <Col lg={6}>
          <Row className="justify-content-center">
            <IconHolder icon={Step10Icon} viewBox="0 0 25 25" color='primary' />
          </Row>
          <h4 className="headerTitle">Securing Your Coverage Today</h4>
          <h5 className="headerSubtitle">You'll receive your policy after payment and signatures are received.</h5>

            <Form.Group>
              <SelectInput
                id="payor"
                name="payor"
                value={props.formState.payor}
                onChange={e => handleValueChange('payor', e.target.value)}
                label="Who is paying?"
                selectOptions={[{ value: "Self", label: "Self" }, { value: "Company", label: "Company" }]}
                errors={errors.payor}
                inputRef={register}
              />
            </Form.Group>

            {props.formState.payor === "Company" &&
              <React.Fragment>
                <Form.Group>
                  <StyledRadioInput
                    name="signingAuthority"
                    id="signingAuthority"
                    label="The proposed insured has signing authority for the company selected as Payor?"
                    value={props.formState.signingAuthority}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('signingAuthority', e)}
                    errors={errors.signingAuthority}
                  />

                </Form.Group>

                <Form.Group>
                  <TextInput
                    name="companyName"
                    id="companyName"
                    label="Company Name"
                    value={props.formState.companyName}
                    onChange={(e) => props.onFieldChange('companyName', e.target.value)}
                    errors={errors.companyName}
                    inputRef={register}
                  />
                </Form.Group>

                <Form.Group>
                  <TextInput
                    name="payorMailingStreet"
                    id="payorMailingStreet"
                    label="Mailing Address"
                    value={props.formState.payorMailingStreet}
                    onChange={e => props.onFieldChange('payorMailingStreet', e.target.value)}
                    errors={errors.payorMailingStreet}
                    inputRef={register}
                  />
                </Form.Group>

                <Form.Group>
                  <TextInput
                    name="payorMailingCity"
                    id="payorMailingCity"
                    label="City"
                    value={props.formState.payorMailingCity}
                    onChange={e => props.onFieldChange('payorMailingCity', e.target.value)}
                    errors={errors.payorMailingCity}
                    inputRef={register}
                  />
                </Form.Group>

                <Row>
                  <Col>
                    <Form.Group>
                      <SelectInput
                        name="payorMailingState"
                        id="payorMailingState"
                        value={props.formState.payorMailingState}
                        onChange={(e) => handleValueChange('payorMailingState', e.target.value)}
                        label="State"
                        selectOptions={StatesWithDisabled(["NY"])}
                        errors={errors.payorMailingState}
                        inputRef={register}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <TextInput
                        name="payorMailingZip"
                        id="payorMailingZip"
                        label="Zip"
                        value={props.formState.payorMailingZip}
                        onChange={e => props.onFieldChange('payorMailingZip', e.target.value)}
                        errors={errors.payorMailingZip}
                        inputRef={register}
                        InputProps={{
                          inputComponent: InputMaskZip,
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group>
                  <StyledRadioInput
                    name="mailingAdressISPOBox"
                    id="mailingAdressISPOBox"
                    label="Is mailing address a PO Box?"
                    value={props.formState.mailingAdressISPOBox}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('mailingAdressISPOBox', e)}
                    errors={errors.mailingAdressISPOBox}
                  />
                </Form.Group>

                {(props.formState.mailingAdressISPOBox === "true") &&

                  <React.Fragment>
                    <Form.Group>
                      <TextInput
                        name="payorStreetAdress"
                        id="payorStreetAdress"
                        label="Street Address"
                        value={props.formState.payorStreetAdress}
                        onChange={e => props.onFieldChange('payorStreetAdress', e.target.value)}
                        errors={errors.payorStreetAdress}
                        inputRef={register}
                      />
                    </Form.Group>

                    <Form.Group>
                      <TextInput
                        name="payorStreetCity"
                        id="payorStreetCity"
                        label="City"
                        value={props.formState.payorStreetCity}
                        onChange={e => props.onFieldChange('payorStreetCity', e.target.value)}
                        errors={errors.payorStreetCity}
                        inputRef={register}
                      />
                    </Form.Group>

                    <Row>
                      <Col>
                        <SelectInput
                          name="payorStreetState"
                          id="payorStreetState"
                          value={props.formState.payorStreetState}
                          onChange={(e) => handleValueChange('payorStreetState', e.target.value)}
                          label="State"
                          selectOptions={StatesWithDisabled(["NY"])}
                          errors={errors.payorStreetState}
                          inputRef={register}
                        />
                      </Col>
                      <Col>
                        <Form.Group>
                          <TextInput
                            name="payorStreetZip"
                            id="payorStreetZip"
                            label="Zip"
                            value={props.formState.payorStreetZip}
                            onChange={e => props.onFieldChange('payorStreetZip', e.target.value)}
                            errors={errors.payorStreetZip}
                            inputRef={register}
                            InputProps={{
                              inputComponent: InputMaskZip,
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                  </React.Fragment>
                }
              </React.Fragment>
            }

            {props.formState.payor === "Company" && (
              <Form.Group>
                <TextInput
                  name="payorPhoneNumber"
                  id="payorPhoneNumber"
                  label="Phone Number"
                  value={props.formState.payorPhoneNumber}
                  onChange={(e) => handleValueChange('payorPhoneNumber', e.target.value)}
                  errors={errors.payorPhoneNumber}
                  inputRef={register}
                  InputProps={{
                    inputComponent: InputMaskPhone,
                  }}
                />
              </Form.Group>
            )}

            <Form.Group>
              <TextInput
                name="routingNumber"
                id="routingNumber"
                label="Routing Number"
                value={props.formState.routingNumber}
                onChange={(e) => handleValueChange('routingNumber', e.target.value)}
                errors={errors.routingNumber}
                inputRef={register}
              />
            </Form.Group>

            {
              hasRoutingNumberError && (
                <div ref={routingErrorMessage} className="text-danger text-center mb-3">Unable to verify routing number.</div>)
            }

            <Form.Group className="mb-1">
              <TextInput
                name="accountNumber"
                id="accountNumber"
                label="Account Number"
                value={props.formState.accountNumber}
                onChange={(e) => handleValueChange('accountNumber', e.target.value)}
                errors={errors.accountNumber}
                inputRef={register}
              />
            </Form.Group>

            <Row className="justify-content-center pb-3 pt-3">
              <Button type="button" className="btn btn-link" onClick={() => { setHelpDialogOpen(true) }} color="primary"><h6 className="mb-0">WHERE TO FIND THIS? </h6></Button>
            </Row>

            <Form.Group>
              <TextInput
                name="institutionName"
                id="institutionName"
                label="Institution Name"
                value={props.formState.institutionName}
                disabled={true}
                onChange={(e) => handleValueChange('institutionName', e.target.value)}
                errors={errors.institutionName}
                inputRef={register}
                InputProps={{
                  endAdornment: routingLoading
                }}
              />
            </Form.Group>

            <Form.Group>
              <SelectInput
                id="accountType"
                name="accountType"
                value={props.formState.accountType}
                onChange={e => handleValueChange('accountType', e.target.value)}
                label="Account Type"
                selectOptions={[{ value: "Savings Account", label: "Savings Account" }, { value: "Checking Account", label: "Checking Account" }]}
                errors={errors.accountType}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <StyledRadioInput
                name="businessAccount"
                id="businessAccount"
                label="Is this a Business Account?"
                value={props.formState.businessAccount}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('businessAccount', e)}
                errors={errors.businessAccount}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="nameOnAccount"
                id="nameOnAccount"
                label="Name on Account"
                value={props.formState.nameOnAccount}
                onChange={(e) => handleValueChange('nameOnAccount', e.target.value)}
                errors={errors.nameOnAccount}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <CheckboxInput
                name="paymentAgree"
                id="paymentAgree"
                checked={ConvertStringToBoolean(props.formState.paymentAgree)}
                onChange={e => handleValueChange('paymentAgree', ConvertBooleanToString(e.target.checked))}
                errors={errors.paymentAgree}
                inputRef={register}
                label="I agree to make payments from this account to cover the cost of this insurance."
              />
            </Form.Group>

            <Form.Group>
              <RadioInput
                name="draftPreference"
                id="draftPreference"
                label="What is your payment preference?"
                value={props.formState.draftPreference}
                radios={[{ value: "Today", label: "Pay and start coverage today" },
                { value: "Specific", label: "Specific start date" }]}
                onChange={e => handleValueChange('draftPreference', e.target.value)}
                errors={errors.draftPreference}
                inputRef={register}
              />
            </Form.Group>

            {props.formState.draftPreference === "Specific" &&
              <Form.Group>
                <TextInput
                  name="specificStartDate"
                  id="specificStartDate"
                  label="Start Date"
                  value={props.formState.specificStartDate}
                  errors={errors && errors.specificStartDate}
                  inputRef={props.register}
                  onChange={(e) => handleValueChange('specificStartDate', e.target.value)}
                  InputProps={{
                    inputComponent: InputMaskDate,
                  }}
                />
              </Form.Group>
            }
        </Col>
      </Row>
      <BackNext onBack={props.onBack} disabled={loading || (routingLoading !== '')} loading={loading} colSize={2} continueLabel={{ text: 'Next' }}/>
      </Form>
      <HelpDialog
        open={helpDialogOpen}
        onHelpDialogClosed={() => {
          setHelpDialogOpen(false);
        }}
      />
    </Container>
  )
}

function HelpDialog(props) {
  return (
    <div>
      <Dialog maxWidth="sm" className="help-dialog" open={props.open} onClose={props.onHelpDialogClosed} scroll="body">
        <DialogContent className="product-info-dialog-content pt-1">
          <IconButton className="product-info-close-button" aria-label="close" onClick={props.onHelpDialogClosed}>
            <CloseIcon />
          </IconButton>
          <br />
          <Row className="justify-content-center image-check">
            <img src={process.env.PUBLIC_URL + '/americo/check.png'} alt="Check" />
          </Row>
        </DialogContent>
      </Dialog>
    </div>
  );
}

FinancialInformation.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

FinancialInformation.ValidationSchema = (ageSpecs, piBirthdate) => {
  return object().shape({
    payor: string().required('Payor is required.'),
    signingAuthority: string().when('payor', {
      is: "Company",
      then: string().required('This field is required.').matches(/^true/, 'The insured must have signing authority to name Company as payor.'),
      otherwise: string().notRequired()
    }),
    companyName: string().when('payor', {
      is: "Company",
      then: string().required('Company Name is required.'),
      otherwise: string().notRequired()
    }),
    payorMailingStreet: string().when('payor', {
      is: "Company",
      then: customValidators.string().isValidAddress('Invalid address. No special character is allowed').required('Mailing Address is required.'),
      otherwise: string().notRequired()
    }),
    payorMailingCity: string().when('payor', {
      is: "Company",
      then: string().required('City is required.'),
      otherwise: string().notRequired()
    }),
    payorMailingState: string().when('payor', {
      is: "Company",
      then: string().required('State is required.'),
      otherwise: string().notRequired()
    }),
    payorMailingZip: string().when('payor', {
      is: "Company",
      then: customValidators.string().isValidNumbersOnly('Invalid zip', 5).required('Zip is required.'),
      otherwise: string().notRequired()
    }),
    mailingAdressISPOBox: string().when('payor', {
      is: "Company",
      then: string().required('This field is required.'),
      otherwise: string().notRequired()
    }),
    payorStreetAdress: string().when(['mailingAdressISPOBox', 'payor'], {
      is: (mailingAdressISPOBox, payor) => mailingAdressISPOBox === "true" && payor === "Company",
      then: customValidators.string().isValidAddress('Invalid address. No special character is allowed').required('Street Adress is required.'),
      otherwise: string().notRequired()
    }),
    payorStreetCity: string().when(['mailingAdressISPOBox', 'payor'], {
      is: (mailingAdressISPOBox, payor) => mailingAdressISPOBox === "true" && payor === "Company",
      then: string().required('City is required.'),
      otherwise: string().notRequired()
    }),
    payorStreetState: string().when(['mailingAdressISPOBox', 'payor'], {
      is: (mailingAdressISPOBox, payor) => mailingAdressISPOBox === "true" && payor === "Company",
      then: string().required('State is required.'),
      otherwise: string().notRequired()
    }),
    payorStreetZip: string().when(['mailingAdressISPOBox', 'payor'], {
      is: (mailingAdressISPOBox, payor) => mailingAdressISPOBox === "true" && payor === "Company",
      then: customValidators.string().isValidNumbersOnly('Invalid zip', 5).required('Zip is required.'),
      otherwise: string().notRequired()
    }),
    routingNumber: string()
      .required('Routing Number is required.')
      .matches(/^[0-9]{9}$/, 'The routing number must be 9 digits in length.'),
    accountNumber: string()
      .required('Account Number is required.')
      .matches(/^[0-9]{4,17}$/, 'The account number must be between 4 and 17 digits in length.'),
    accountType: string().required('Account Type is required.'),
    businessAccount: string().required('This field is required.'),
    nameOnAccount: string().notRequired(),
    paymentAgree: mixed().oneOf([true], 'You must agree to the payment authorization.'),
    draftPreference: string().required('This field is required.'),
    specificStartDate: string().when('draftPreference', {
      is: "Specific",
      then: customValidators.string().required('Specific start date is required.').isValidSpecificStartDate('Start date must be within 30 days and cannot be on the 29th, 30th, or 31st of the month.', piBirthdate),
      otherwise: string().notRequired()
    }),
    payorPhoneNumber: string().when('payor', {
      is: "Company",
      then: customValidators.string()
        .required('Phone Number is required.')
        .isValidPhone('Phone Number must be formatted as "(123) 456-7890".'),
      otherwise: string().notRequired()
    }),
  })
};

FinancialInformation.DefaultFormState = (prefill = false) => {
  return {
    payor: prefill ? 'Company' : '',
    signingAuthority: prefill ? 'true' : '',
    companyName: prefill ? 'Covr' : '',
    payorMailingStreet: prefill ? "2020 Covr St" : "",
    payorMailingCity: prefill ? "Boise" : "",
    payorMailingState: prefill ? "MA" : "",
    payorMailingZip: prefill ? "83702" : "",
    mailingAdressISPOBox: prefill ? "false" : "",
    payorStreetAdress: prefill ? "2020 Covr St" : "",
    payorStreetCity: prefill ? "Boise" : "",
    payorStreetState: prefill ? "MA" : "",
    payorStreetZip: prefill ? "83702" : "",
    lengthOfTimeAtCurrentAddress: "",
    payorPriorMailingStreet: "",
    payorPriorMailingCity: "",
    payorPriorMailingState: "",
    payorPriorMailingZip: "",
    routingNumber: prefill ? '081218879' : '',
    accountNumber: prefill ? '11111111112' : '',
    institutionName: prefill ? '' : '',
    accountType: prefill ? 'Savings Account' : '',
    businessAccount: prefill ? 'true' : '',
    nameOnAccount: prefill ? 'Props' : '',
    paymentAgree: prefill ? 'true' : 'false',
    draftPreference: prefill ? 'Specific' : 'Pay and start coverage today',
    specificStartDate: CalculatePolicyEffectiveDate(new Date()),
    payorPhoneNumber: prefill ? "(123) 345-4567" : "",
  };
};


// WEBPACK FOOTER //
// ./src/components/Quoters/AmericoLoanProtection/steps/FinancialInformation.js